import { httpsCallable } from 'firebase/functions';
import { functions } from '../../constants/firebaseModular';
import type {
  AgentType,
  HandleAgentRequestPayload,
  HandleAgentRequestResponse,
  HandleAgentRequestCreateResponse,
  HandleAgentRequestMutationResponse,
} from '@usacsmideo/types';

/**
 * ECN Subtask 9 (CU-86ahjxrgg) — client wrappers over the `handleAgentRequest`
 * callable. The backend is a single dispatch-on-`action` function; we expose one
 * named helper per action the patient/agent UI actually performs:
 *   - inviteAgent  (action: "create")  — patient invites an HCA or alternate.
 *   - archiveAgentRequest (action: "archive") — patient "Remove" OR agent
 *     "Step down". The user-facing verb differs by persona (redesign §3.1); the
 *     wire action is `archive` either way, and the backend keys the three-way
 *     notify off `archivedBy` (the authenticated caller).
 *
 * `accept` is deliberately absent — acceptance is SSR-only (`/invite`).
 */

function callHandleAgentRequest() {
  return httpsCallable<HandleAgentRequestPayload, HandleAgentRequestResponse>(
    functions,
    'handleAgentRequest',
  );
}

/**
 * Patient invites an agent (HCA or alternate).
 *
 * `consentAffirmed` + `consentVersion` carry the patient's affirmation of the
 * displayed P1 consent (ECN P2.2, CU-86ajbm1e6). The backend REJECTS a create
 * that is unaffirmed or whose `consentVersion` drifts from the manifest (P2.1);
 * callers must source `consentVersion` from `patientAuthorizationConsentVersion()`
 * in `@usacsmideo/types` — the same builder the server drift-checks against — so
 * displayed == recorded by construction. Both are required on the wire.
 */
export const inviteAgent = async (params: {
  agentType: AgentType;
  recipientEmail: string;
  recipientName?: string;
  consentAffirmed: boolean;
  consentVersion: string;
}): Promise<HandleAgentRequestCreateResponse> => {
  const response = await callHandleAgentRequest()({
    action: 'create',
    agentType: params.agentType,
    recipientEmail: params.recipientEmail,
    recipientName: params.recipientName,
    consentAffirmed: params.consentAffirmed,
    consentVersion: params.consentVersion,
  });
  return response.data as HandleAgentRequestCreateResponse;
};

/**
 * Archive an agent relationship by doc id. Used for BOTH patient "Remove" (a
 * request they sent) and agent "Step down" (a request they accepted) — the
 * backend authorizes the caller (sender / recipient / admin) against the loaded
 * request and records `archivedBy`.
 */
export const archiveAgentRequest = async (
  agentRequestId: string,
): Promise<HandleAgentRequestMutationResponse> => {
  const response = await callHandleAgentRequest()({
    action: 'archive',
    agentRequestId,
  });
  return response.data as HandleAgentRequestMutationResponse;
};
